import { Card } from "shared/components/Card";
import { Switch } from "shared/components/forms/Switch";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { Unit } from "types/landlord/address";
import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import addressApi from "api/landlord/address";
import { toast } from "react-toastify";
import { getErrorMessage } from "utils/error";
import { AllowOrDisallowApplications } from "shared/app/AllowOrDisallowApplications";
import { useSettingsController } from "views/settings-integration/controller";
import { PropertyKind } from "shared/types";
import { Hide, Show } from "shared/components";

interface ListingSectionProps {
  propertyId: number;
  propertyKind: PropertyKind | null;
  unit: Unit;
}

export const ListingSection = ({
  propertyId,
  propertyKind,
  unit
}: ListingSectionProps) => {
  const dictionary = useUnitsPropertiesDictionary();
  const queryClient = useQueryClient();
  const { isEverySyncEnabled } = useSettingsController();

  const [linkIsActive, setLinkIsActive] = useState<boolean>(
    Boolean(unit.magic_link?.active)
  );
  const [unitListed, setUnitListed] = useState<boolean>(Boolean(unit.listed));

  useEffect(() => {
    setLinkIsActive(Boolean(unit.magic_link?.active));
    setUnitListed(Boolean(unit.listed));
  }, [unit.magic_link?.active, unit.listed]);

  const enableAllowApplying = () => {
    setLinkIsActive(true);
  };

  const disableAllowApplying = () => {
    if (propertyKind === null && unitListed) {
      setLinkIsActive(false);
      setUnitListed(false);
    } else setLinkIsActive(false);
  };

  const enableShowOnPortfolio = () => {
    if (propertyKind === null && !linkIsActive) {
      setUnitListed(true);
      setLinkIsActive(true);
    } else setUnitListed(true);
  };

  const disableShowOnPortfolio = () => {
    setUnitListed(false);
  };

  const { mutate: updateUnitSettings, isPending } = useMutation({
    mutationFn: async () => {
      if (unit.magic_link?.id)
        await addressApi.updateMagicLink(unit.magic_link.id, {
          active: linkIsActive,
          listed: unitListed
        });
    },
    onSuccess: () => {
      propertyKind === PropertyKind.SingleFamily
        ? queryClient.invalidateQueries({
            queryKey: ["properties", String(propertyId)]
          })
        : queryClient.invalidateQueries({
            queryKey: ["unit", String(propertyId), String(unit.id)]
          });
    },
    onError: (error) => {
      toast.error(`Failed to update settings: ${getErrorMessage(error)}`);
    }
  });

  useEffect(() => {
    if (
      linkIsActive !== Boolean(unit.magic_link?.active) ||
      unitListed !== Boolean(unit.listed)
    )
      updateUnitSettings();
  }, [linkIsActive, unitListed]);

  return (
    <Card>
      <h3 className="text-base font-bold py-6 px-8 border-b border-alpha-40">
        Listing
      </h3>
      <div className="space-y-6 py-6 px-8">
        {isEverySyncEnabled && unit.pms_id ? (
          <AllowOrDisallowApplications
            isActive={linkIsActive}
            isListed={unitListed}
            withListedInfo
          />
        ) : (
          <>
            {/* For the default property, the two toggles are dependent on each other */}
            <Show if={propertyKind === null}>
              <div>
                <Switch
                  label="Allow applying"
                  checked={linkIsActive}
                  className="text-base font-medium text-content-1000"
                  onChange={() => {
                    if (linkIsActive) disableAllowApplying();
                    else enableAllowApplying();
                  }}
                />

                <div className="relative">
                  {/*dashed line*/}
                  <div className="absolute border-l border-content-100 left-5 border-dashed h-5 mt-1" />
                  {/*circle*/}
                  <div className="absolute transform -translate-y-1/2 left-10 top-7 size-1.5 bg-content-100 rounded-full" />
                  {/*dashed line*/}
                  <div className="absolute transform -translate-y-1/2 left-5 top-7 border-dashed border-content-100 border-t w-6" />
                </div>

                <div className="mt-4 ml-14">
                  <Switch
                    label={`Show ${dictionary.unit} on portfolio page`}
                    checked={unitListed}
                    className="text-base font-medium text-content-1000"
                    onChange={() => {
                      if (unitListed) disableShowOnPortfolio();
                      else enableShowOnPortfolio();
                    }}
                  />
                </div>
              </div>
            </Show>

            {/* For the single family/multi family property, the two toggles are independent */}
            <Hide if={propertyKind === null}>
              <div>
                <Switch
                  label={`List ${propertyKind === PropertyKind.SingleFamily ? dictionary.property : dictionary.unit}`}
                  checked={unitListed}
                  className="text-base font-medium text-content-1000"
                  onChange={() => {
                    setUnitListed((prev) => !prev);
                  }}
                  disabled={isPending}
                />
              </div>
              <div>
                <Switch
                  label={`Allow applying for ${dictionary.unit}`}
                  checked={linkIsActive}
                  className="text-base font-medium text-content-1000"
                  onChange={() => {
                    setLinkIsActive((prev) => !prev);
                  }}
                  disabled={isPending}
                />
              </div>
            </Hide>
          </>
        )}
      </div>
    </Card>
  );
};
